<script lang="ts">
    import { me } from "@frontend/user";
    import { onMount } from "svelte";
    import { service } from "@frontend/services";
    import { GetToken } from "@frontend/user";
    import type { GroupDTO, GameRound } from "@frontend/walletapi";
    import { players } from "@frontend/UnassingnedUser";
    import { push } from "svelte-spa-router";
    import { translate, currentLanguage } from "../Translator";

    
    // Función para obtener la fecha actual en formato YYYY-MM-DD considerando la zona horaria local
    function getLocalDate() {
        const date = new Date();
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    }

    // Inicializar fechas con la fecha local
    let startDate: string;
    let endDate: string;
    let loading: boolean = false;
    let error: string | null = null;
    let linkLoading: number | null = null;
    let groups: GroupDTO[] = [];
    let selectedGroupId: number | null = $me?.GroupId || null;
    let filteredGroups: GroupDTO[] = [];
    let gameRounds: GameRound[] = []; 
    let currentPage = 1;
    let totalPages = 1;
    let totalItems = 0;

    async function fetchGroups() {
        loading = true;
        error = null;
        try {
            const token = GetToken();
            if (!token) {
                error = "No hay sesión activa";
                return;
            }

            const result = await service.walletapi.groupList({
                Token: token
            });

            if (!result.Success) {
                error = result.ErrorMessage || "Error al obtener los grupos";
                return;
            }

            groups = result.Data || [];
            console.log("Grupos obtenidos:", groups);
            
            // Asegurarnos de que selectedGroupId esté establecido
            if (!selectedGroupId && $me) {
                if ($me.Role === "Owner" && groups.length > 0) {
                    selectedGroupId = groups[0].Id; // Para Owner, seleccionar el primer grupo por defecto
                } else if ($me.GroupId) {
                    selectedGroupId = $me.GroupId;
                }
            }
            
            // Realizar búsqueda inicial
            if (selectedGroupId || $me?.Role === "Owner") {
                await handleSearch();
            }
        } catch (err) {
            console.error("Error al obtener grupos:", err);
            error = "Error al cargar los grupos";
        } finally {
            loading = false;
        }
    }

    function filterGroupsByRole() {
        if (!$me) return [];
        
        // Si es Owner o Admin, puede ver todos los grupos
        if ($me.Role === "Owner" || $me.Role === "Admin") {
            return groups;
        }
        
        // Para otros roles, solo ven su grupo asignado
        return groups.filter(group => group.Id === $me?.GroupId);
    }

    async function fetchGameRounds() {
        if (!startDate || !endDate) {
            return;
        }

        loading = true;
        error = null;

        try {
            const token = GetToken();
            if (!token) {
                error = "No hay sesión activa";
                return;
            }

            // Para Owner, enviamos todos los IDs de grupos
            const groupIds = $me?.Role === "Owner" ? 
                groups.map(g => g.Id) :  // Lista de todos los IDs de grupos
                (selectedGroupId ? [selectedGroupId] : [$me?.GroupId]);

            // Ajustar las fechas para incluir todo el día
            const startDateTime = new Date(startDate + 'T00:00:00');
            const endDateTime = new Date(endDate + 'T23:59:59.999');

            console.log('Buscando con parámetros:', {
                startDateTime: startDateTime.toISOString(),
                endDateTime: endDateTime.toISOString(),
                groupIds: groupIds,
                isOwner: $me?.Role === "Owner",
                totalGroups: groups.length,
                page: currentPage
            });

            const result = await service.walletapi.gameRound({
                Token: token,
                Data: {
                    Datestart: startDateTime,
                    Dateend: endDateTime,
                    GroupIds: groupIds,
                    UserId: $me?.Id || 0,
                    Page: currentPage
                }
            });

            if (!result.Success) {
                error = result.ErrorMessage || "Error al obtener las rondas de juego";
                return;
            }

            if (result.Data) {
                gameRounds = result.Data.Items || [];
                totalPages = result.Data.TotalPages;
                currentPage = result.Data.CurrentPage;
                totalItems = result.Data.TotalItems;
            } else {
                gameRounds = [];
                totalPages = 1;
                currentPage = 1;
                totalItems = 0;
            }

            console.log("Rondas de juego obtenidas:", {
                rounds: gameRounds,
                currentPage,
                totalPages,
                totalItems
            });
        } catch (err) {
            console.error("Error al obtener rondas de juego:", err);
            error = "Error al cargar las rondas de juego";
        } finally {
            loading = false;
        }
    }

    async function handleSearch() {
        if (!startDate || !endDate) {
            error = "Por favor selecciona ambas fechas";
            return;
        }
        
        if (new Date(startDate) > new Date(endDate)) {
            error = "La fecha inicial no puede ser mayor que la fecha final";
            return;
        }

        // Solo validamos el grupo seleccionado si no es Owner
        if ($me?.Role !== "Owner" && !selectedGroupId) {
            error = "Por favor selecciona un grupo";
            return;
        }

        await fetchGameRounds();
    }

    async function getRoundLink(UserId: number, roundId: string | undefined, CountryCode: string | undefined) {
        console.log('getRoundLink llamado con:', { UserId, roundId, CountryCode });
        
        if (!roundId || !CountryCode) {
            error = "Información de ronda incompleta";
            return;
        }

        linkLoading = UserId;
        try {
            const token = GetToken();
            if (!token) {
                error = "No hay sesión activa";
                return;
            }

            const result = await service.walletapi.getRealLinkRound({
                Token: token,
                Data: {
                    GameRoundId: roundId,
                    CountryCode: CountryCode,
                    UserId: UserId
                }
            });

            if (!result.Success) {
                error = result.ErrorMessage || "Error al obtener el link de la ronda";
                return;
            }

            // Abrir el link en una nueva pestaña
            if (result.Data?.Link) {
                window.open(result.Data.Link, '_blank');
            } else {
                error = "No se pudo obtener el link de la ronda";
            }

        } catch (err) {
            console.error("Error al obtener link de la ronda:", err);
            error = "Error al obtener el link de la ronda";
        } finally {
            linkLoading = null;
        }
    }

    onMount(async () => {
        // Verificar autenticación primero
        if (!$me) {
            push("/user/Login");
            return;
        }

        // Inicializar fechas con la fecha local
        startDate = getLocalDate();
        endDate = getLocalDate();
        
        await fetchGroups();
    });

    $: {
        console.log("Grupos sin filtrar:", groups);
        // Para Owner, mostramos todos los grupos sin filtrar
        filteredGroups = groups;
        console.log("Grupos filtrados:", filteredGroups);
    }
</script>

<div class="container">
    <h1>{$translate('List of Game Rounds')}</h1>
    
    {#if error}
        <div class="error-message">
            {error}
        </div>
    {/if}

    {#if loading}
        <div class="loading">{$translate('Loading')}...</div>
    {/if}

    <div class="filters">
        <div class="group-select-container">
            <div class="select-group">
                <label for="groupSelect">{$translate('Group')}:</label>
                <select 
                    id="groupSelect" 
                    bind:value={selectedGroupId} 
                    class="group-select"
                >
                    <option value={null}>{$translate('Select a group...')}</option>
                    {#each filteredGroups as group}
                        <option value={group.Id}>{group.Name}</option>
                    {/each}
                </select>
            </div>
        </div>

        <div class="date-inputs">
            <div class="input-group">
                <label for="startDate">{$translate('Start Date')}:</label>
                <input 
                    type="date" 
                    id="startDate"
                    bind:value={startDate}
                    class="date-input"
                />
            </div>
            
            <div class="input-group">
                <label for="endDate">{$translate('End Date')}:</label>
                <input 
                    type="date" 
                    id="endDate"
                    bind:value={endDate}
                    class="date-input"
                />
            </div>
        </div>
        
        <button 
            class="search-button" 
            on:click={handleSearch}
            disabled={!selectedGroupId || !startDate || !endDate}
        >
            {$translate('Search')}
        </button>
    </div>

    {#if selectedGroupId && startDate && endDate}
        <div class="game-rounds-results">
            <h2>{$translate('Round of games')} - {groups.find(g => g.Id === selectedGroupId)?.Name}</h2>
            <p>{$translate('Date result')} {startDate} {$translate('to')} {endDate}</p>
            
            <table class="table table-striped">
                <thead>
                    <tr>
                        <th>{$translate('Id')}</th>
                        <th>{$translate('Date')}</th>
                        <th>{$translate('Game')}</th>
                        <th>{$translate('Player')}</th>
                        <th>{$translate('Round Id')}</th>
                        <th>{$translate('Bet')}</th>
                        <th>{$translate('Win')}</th>
                        <th>{$translate('Country code')}</th>
                        <th>{$translate('Actions')}</th>
                    </tr>
                </thead>
                <tbody>
                    {#each gameRounds as round}
                        <tr>
                            <td>{round.Id}</td>
                            <td>{new Date(round.Date).toLocaleString('es-MX', { 
                                year: 'numeric',
                                month: '2-digit',
                                day: '2-digit',
                                hour: '2-digit',
                                minute: '2-digit',
                                second: '2-digit',
                                hour12: false
                            })}</td>
                            <td>{round.Game?.GameName || 'N/A'}</td>
                            <td>{round.Player?.FirstName} {round.Player?.LastName}</td>
                            <td>{round.RoundId}</td>
                            <td>${(round.Bet?.InCents || 0) / 100}</td>
                            <td>${(round.Win?.InCents || 0) / 100}</td>
                            <td>{round.CountryCode}</td>
                            <td>
                                <button 
                                    class="btn btn-primary"
                                    on:click|preventDefault={() => {
                                        console.log('Button clicked, round data:', round);
                                        console.log('Calling getRoundLink with:', {
                                            Id: round.Id,
                                            roundId: round.RoundId,
                                            CountryCode: round.CountryCode
                                        });
                                        getRoundLink(round.Player?.Id, round.RoundId, round.CountryCode);
                                    }}
                                    disabled={linkLoading === round.Id}
                                >
                                    {#if linkLoading === round.Id}
                                        <span class="spinner"></span>
                                    {/if}
                                    {$translate('Round')}
                                </button>
                            </td>
                        </tr>
                    {/each}
                </tbody>
            </table>
            {#if totalPages > 1}
                <div class="pagination">
                    <button 
                        class="pagination-button"
                        disabled={currentPage === 1}
                        on:click={() => {
                            currentPage = Math.max(1, currentPage - 1);
                            handleSearch();
                        }}
                    >
                    {$translate('Previous')}
                    </button>
                    
                    <span class="pagination-info">
                        {$translate('Page')} {currentPage} {$translate('of')} {totalPages} ({totalItems} {$translate('record')})
                    </span>

                    <button 
                        class="pagination-button"
                        disabled={currentPage === totalPages}
                        on:click={() => {
                            currentPage = Math.min(totalPages, currentPage + 1);
                            handleSearch();
                        }}
                    >
                        {$translate('Next')}
                    </button>
                </div>
            {/if}
        </div>
    {/if}
</div>

<style>
    .container {
        padding: 20px;
        max-width: 1200px;
        margin: 0 auto;
    }

    h1, h2 {
        color: #333;
        margin-bottom: 24px;
    }

    .filters {
        background: #f5f5f5;
        padding: 20px;
        border-radius: 8px;
        margin-bottom: 20px;
    }

    .group-select-container {
        margin-bottom: 20px;
    }

    .date-inputs {
        display: flex;
        gap: 20px;
        margin-bottom: 16px;
    }

    .input-group, .select-group {
        flex: 1;
    }

    label {
        display: block;
        margin-bottom: 8px;
        color: #555;
        font-weight: 500;
    }

    .date-input, .group-select {
        width: 100%;
        padding: 8px 12px;
        border: 1px solid #ddd;
        border-radius: 4px;
        font-size: 14px;
        height: 38px;
    }

    .search-button {
        background-color: #4CAF50;
        color: white;
        padding: 10px 20px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.3s;
    }

    .search-button:hover:not(:disabled) {
        background-color: #45a049;
    }

    .search-button:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }

    .error-message {
        color: #dc3545;
        padding: 10px;
        margin-bottom: 20px;
        border: 1px solid #dc3545;
        border-radius: 4px;
        background-color: #f8d7da;
    }

    .loading {
        text-align: center;
        padding: 20px;
        color: #666;
    }

    .game-rounds-results {
        margin-top: 20px;
        padding: 20px;
        background: white;
        border-radius: 8px;
        box-shadow: 0 2px 4px rgba(0,0,0,0.05);
    }

    .table {
        width: 100%;
        border-collapse: collapse;
    }

    .table th, .table td {
        border: 1px solid #ddd;
        padding: 8px;
        text-align: left;
    }

    .table th {
        background-color: #f0f0f0;
    }

    .btn {
        padding: 6px 12px;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
        transition: background-color 0.2s;
    }

    .btn-primary {
        background-color: #007bff;
        color: white;
    }

    .btn-primary:hover {
        background-color: #0056b3;
    }

    .btn:disabled {
        background-color: #ccc;
        cursor: not-allowed;
    }

    .spinner {
        width: 16px;
        height: 16px;
        border: 2px solid #f3f3f3;
        border-top: 2px solid #007bff;
        border-radius: 50%;
        animation: spin 1s linear infinite;
        display: inline-block;
        margin-right: 5px;
    }

    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }

    .pagination {
        display: flex;
        justify-content: center;
        align-items: center;
        gap: 1rem;
        margin-top: 2rem;
        padding: 1rem;
    }

    .pagination-button {
        padding: 0.5rem 1rem;
        background-color: #4CAF50;
        color: white;
        border: none;
        border-radius: 4px;
        cursor: pointer;
        font-size: 14px;
    }

    .pagination-button:disabled {
        background-color: #cccccc;
        cursor: not-allowed;
    }

    .pagination-button:hover:not(:disabled) {
        background-color: #45a049;
    }

    .pagination-info {
        font-size: 14px;
        color: #666;
    }
</style>